import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import { setSession } from "../../slices/sessionSlice";

import type { CefrLevel } from "@nicsmith/shared_types";
import { useUpdateUserMutation } from "../../api/userApi";
import ProfileStep from "./steps/ProfileStep";
import LanguageStep from "./steps/LanguageStep";
import LevelStep from "./steps/LevelStep";
import AvatarStep from "./steps/AvatarStep";
import styles from "./OnboardingWizard.module.scss";
import { trackTutorialComplete } from "../../utils/analytics";

const TOTAL_STEPS = 4;

const OnboardingWizard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.session.user);
  const idToken = useSelector((state: RootState) => state.session.idToken);
  const [updateUser] = useUpdateUserMutation();

  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState<"forward" | "backward">("forward");
  const [saving, setSaving] = useState(false);

  // Pre-fill name from user record (e.g. set during invite acceptance)
  const initialFirst = user?.first_name || "";
  const initialLast = user?.last_name || "";
  // If no first/last but there's a full 'name', split it
  const fallbackName = (!initialFirst && !initialLast && user?.name) ? user.name : "";
  const fallbackParts = fallbackName.trim().split(/\s+/);
  const defaultFirst = initialFirst || fallbackParts[0] || "";
  const defaultLast = initialLast || fallbackParts.slice(1).join(" ") || "";

  // Form state
  const [firstName, setFirstName] = useState(defaultFirst);
  const [lastName, setLastName] = useState(defaultLast);
  const [language, setLanguage] = useState(i18n.language || "en");
  const [level, setLevel] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState("");

  const handleLanguageChange = (code: string) => {
    setLanguage(code);
    i18n.changeLanguage(code);
  };

  const canContinue = () => {
    switch (step) {
      case 0: return !!firstName.trim() && !!lastName.trim();
      case 1: return !!language;
      case 2: return !!level;
      case 3: return !!avatarUrl;
      default: return false;
    }
  };

  const goNext = () => {
    if (step < TOTAL_STEPS - 1) {
      setDirection("forward");
      setStep((s) => s + 1);
    }
  };

  const goBack = () => {
    if (step > 0) {
      setDirection("backward");
      setStep((s) => s - 1);
    }
  };

  const handleFinish = async () => {
    if (!user || !idToken) return;
    setSaving(true);

    try {
      const result = await updateUser({
        id: `uid/${user.uid}`,
        data: {
          ...user,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          preferred_language: language,
          avatar: avatarUrl,
          onboarding_completed: true,
          profile: {
            ...user.profile,
            level: level as CefrLevel,
            target_language_code: "de",
          },
        },
      }).unwrap();

      localStorage.setItem("dmyTourPending", "true");
      trackTutorialComplete();
      dispatch(setSession({ user: result, idToken }));
    } catch {
      setSaving(false);
    }
  };

  const isLastStep = step === TOTAL_STEPS - 1;

  return (
    <div className={styles.wrapper}>
      {/* Progress bar */}
      <div className={styles.progressBar}>
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <div
            key={i}
            className={`${styles.progressSegment} ${i <= step ? styles.progressActive : ""}`}
          />
        ))}
      </div>

      <div className={styles.wizardCard}>
        <div
          className={`${styles.stepContainer} ${direction === "forward" ? styles.slideInRight : styles.slideInLeft}`}
          key={step}
        >
          {step === 0 && (
            <ProfileStep
              firstName={firstName}
              lastName={lastName}
              onFirstNameChange={setFirstName}
              onLastNameChange={setLastName}
            />
          )}
          {step === 1 && (
            <LanguageStep value={language} onChange={handleLanguageChange} />
          )}
          {step === 2 && <LevelStep value={level} onChange={setLevel} />}
          {step === 3 && (
            <AvatarStep value={avatarUrl} onChange={setAvatarUrl} />
          )}
        </div>

        <div className={styles.navigation}>
          {step > 0 ? (
            <button
              type="button"
              className={styles.backButton}
              onClick={goBack}
            >
              {t("onboarding.back")}
            </button>
          ) : (
            <div />
          )}

          {isLastStep ? (
            <button
              type="button"
              className={styles.primaryButton}
              disabled={!canContinue() || saving}
              onClick={handleFinish}
            >
              {saving ? t("onboarding.saving") : t("onboarding.getStarted")}
            </button>
          ) : (
            <button
              type="button"
              className={styles.primaryButton}
              disabled={!canContinue()}
              onClick={goNext}
            >
              {t("onboarding.continue")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
